import './Dashboard.css';

import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {apiService} from '../../../apiService';
import {useAuth} from '../../../contexts/AuthContext';
import {useDashboardReport} from '../../../hooks/useDashboardReport';
import {useUserInfo} from '../../../hooks/useUserInfo';
import {MONTH_NAMES_SHORT, parseISOWithoutConversion} from '../../../utils';
import {ChartOrMessage} from '../../common';
import {useDateRange} from './useDateRange';

const Dashboard = () => {
  const {currentUser} = useAuth();
  const [filterOpen, setFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: '',
    end: '',
    isAllTime: true,
  });
  const [tempDateRange, setTempDateRange] = useState({
    start: '',
    end: '',
    isAllTime: true,
  });
  const [selectedSuborgs, setSelectedSuborgs] = useState([]);
  const [tempSelectedSuborgs, setTempSelectedSuborgs] = useState([]);
  const [availableSuborgs, setAvailableSuborgs] = useState([]);
  const [dateError, setDateError] = useState(null);
  const {
    isMultiOrg,
    isLoading: userInfoLoading,
    error: userInfoError,
  } = useUserInfo();
  const [popupPos, setPopupPos] = useState({top: 0, left: 0});
  const buttonRef = useRef(null);

  const {loading, error, recordingsData} = useDashboardReport({
    uid: currentUser?.uid,
    startDate: dateRange.start,
    endDate: dateRange.end,
    enabled: !userInfoLoading && !userInfoError && !!currentUser?.uid,
  });

  const getDateRangeText = () => {
    let dateText = '';

    if (dateRange.isAllTime) {
      if (recordingsData.length > 0) {
        const dates = recordingsData.map((record) => {
          const parsed = parseISOWithoutConversion(
            record.checkin_datetime_in_org_zone,
          );
          return parsed ? parsed.toDate().getTime() : 0;
        });
        const firstDate = new Date(Math.min(...dates));
        const endDate = new Date();

        const formattedStart = `${MONTH_NAMES_SHORT[firstDate.getMonth()]} ${firstDate.getFullYear()}`;
        const formattedEnd = `${MONTH_NAMES_SHORT[endDate.getMonth()]} ${endDate.getFullYear()}`;

        dateText = `All time, ${formattedStart} - ${formattedEnd}`;
      } else {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 11);

        const formattedStart = `${MONTH_NAMES_SHORT[startDate.getMonth()]} ${startDate.getFullYear()}`;
        const formattedEnd = `${MONTH_NAMES_SHORT[endDate.getMonth()]} ${endDate.getFullYear()}`;

        dateText = `All time, ${formattedStart} - ${formattedEnd}`;
      }
    } else {
      let startText = '';
      let endText = '';

      if (dateRange.start) {
        const [startYear, startMonth, startDay] = dateRange.start
          .split('-')
          .map((num) => parseInt(num, 10));
        startText = `${MONTH_NAMES_SHORT[startMonth - 1]} ${startDay} ${startYear}`;
      }

      if (dateRange.end) {
        const [endYear, endMonth, endDay] = dateRange.end
          .split('-')
          .map((num) => parseInt(num, 10));
        endText = `${MONTH_NAMES_SHORT[endMonth - 1]} ${endDay} ${endYear}`;
      }

      dateText = `${startText} - ${endText}`;
    }

    // Add suborg filter info for admin users
    if (
      isMultiOrg &&
      selectedSuborgs.length > 0 &&
      selectedSuborgs.length < availableSuborgs.length
    ) {
      const suborgText =
        selectedSuborgs.length === 1
          ? selectedSuborgs[0]
          : `${selectedSuborgs.length} locations`;
      dateText += ` | ${suborgText}`;
    }

    return dateText;
  };
  const {metrics, filteredData} = useDateRange({
    recordingsData,
    dateRange,
    selectedSuborgs,
    isMultiOrg,
  });

  // Fetch suborgs for admin users when recordingsData loads
  useEffect(() => {
    const fetchSuborgs = async () => {
      if (
        !isMultiOrg ||
        !currentUser?.uid ||
        availableSuborgs.length > 0
      ) {
        return;
      }

      try {
        const employeesUrl = `/api/get-manager-employees?uid=${currentUser.uid}`;
        const employeesResponseData = await apiService.get(employeesUrl);

        if (employeesResponseData && employeesResponseData.body) {
          const allSuborgs = [
            ...new Set(
              employeesResponseData.body
                .filter((emp) => emp.user_type !== 'admin')
                .map((emp) => emp.company_name)
                .filter(Boolean),
            ),
          ];
          setAvailableSuborgs(allSuborgs.sort());
          setSelectedSuborgs(allSuborgs);
          setTempSelectedSuborgs(allSuborgs);
        } else if (recordingsData.length > 0) {
          const suborgs = [
            ...new Set(
              recordingsData
                .map((record) => record.company_name)
                .filter(Boolean),
            ),
          ];
          setAvailableSuborgs(suborgs.sort());
          setSelectedSuborgs(suborgs);
          setTempSelectedSuborgs(suborgs);
        }
      } catch (employeeErr) {
        console.warn(
          'Failed to fetch suborgs from employee endpoint, using dashboard data:',
          employeeErr,
        );
        if (recordingsData.length > 0) {
          const suborgs = [
            ...new Set(
              recordingsData
                .map((record) => record.company_name)
                .filter(Boolean),
            ),
          ];
          setAvailableSuborgs(suborgs.sort());
          setSelectedSuborgs(suborgs);
          setTempSelectedSuborgs(suborgs);
        }
      }
    };

    fetchSuborgs();
  }, [
    isMultiOrg,
    currentUser?.uid,
    recordingsData,
    availableSuborgs.length,
  ]);

  const handleFilterClick = () => {
    if (!filterOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPos({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
      });
    }
    if (!filterOpen) {
      setTempDateRange({...dateRange});
      if (isMultiOrg) {
        setTempSelectedSuborgs([...selectedSuborgs]);
      }
      setDateError(null);
    }
    setFilterOpen(!filterOpen);
  };

  const handleDateChange = (e) => {
    const {name, value} = e.target;

    setTempDateRange((prev) => ({
      ...prev,
      [name]: value,
      isAllTime: false,
    }));

    setDateError(null);
  };

  const handleSuborgChange = (suborg) => {
    setTempSelectedSuborgs((prev) => {
      if (prev.includes(suborg)) {
        return prev.filter((s) => s !== suborg);
      } else {
        return [...prev, suborg];
      }
    });
  };

  const handleSelectAllSuborgs = () => {
    if (tempSelectedSuborgs.length === availableSuborgs.length) {
      setTempSelectedSuborgs([]);
    } else {
      setTempSelectedSuborgs([...availableSuborgs]);
    }
  };

  const handleFilterApply = () => {
    const newDateRange = {...tempDateRange};

    let startDate, endDate;
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (newDateRange.start) {
      const [startYear, startMonth, startDay] = newDateRange.start
        .split('-')
        .map((num) => parseInt(num, 10));
      startDate = new Date(startYear, startMonth - 1, startDay);
    }

    if (newDateRange.end) {
      const [endYear, endMonth, endDay] = newDateRange.end
        .split('-')
        .map((num) => parseInt(num, 10));
      endDate = new Date(endYear, endMonth, endDay);
    }

    if (
      newDateRange.start &&
      startDate &&
      !isNaN(startDate.getTime()) &&
      startDate > today
    ) {
      setDateError('Start date cannot be in the future');
      return;
    }

    if (
      newDateRange.start &&
      !isNaN(startDate.getTime()) &&
      newDateRange.end &&
      !isNaN(endDate.getTime())
    ) {
      if (endDate < startDate) {
        setDateError('End date must be after start date');
        return;
      }
    }

    if (!newDateRange.start && !newDateRange.isAllTime) {
      const orgStartDate = new Date(2024, 4, 1);
      const year = orgStartDate.getFullYear();
      const month = (orgStartDate.getMonth() + 1).toString().padStart(2, '0');
      const day = orgStartDate.getDate().toString().padStart(2, '0');
      newDateRange.start = `${year}-${month}-${day}`;
    }

    if (!newDateRange.end && !newDateRange.isAllTime) {
      const todayLocal = new Date();
      const year = todayLocal.getFullYear();
      const month = (todayLocal.getMonth() + 1).toString().padStart(2, '0');
      const day = todayLocal.getDate().toString().padStart(2, '0');
      newDateRange.end = `${year}-${month}-${day}`;
    }

    setDateRange(newDateRange);
    if (isMultiOrg) {
      setSelectedSuborgs([...tempSelectedSuborgs]);
    }
    setFilterOpen(false);
  };

  const handleReset = () => {
    const resetFilter = {
      start: '',
      end: '',
      isAllTime: true,
    };

    setTempDateRange(resetFilter);
    setDateRange(resetFilter);
    if (isMultiOrg) {
      setTempSelectedSuborgs([...availableSuborgs]);
      setSelectedSuborgs([...availableSuborgs]);
    }
    setDateError(null);
    setFilterOpen(false);
  };

  const handleCancel = () => {
    setFilterOpen(false);
    setDateError(null);
  };

  const complianceChartData = filteredData.complianceData;
  const flaggedChartData = filteredData.flaggedData;

  const maxLabelLength = filteredData.frequentlyFlaggedData
    ? Math.max(
        ...filteredData.frequentlyFlaggedData.map((d) => d.name.length),
        0,
      )
    : 0;
  const xAxisHeight = Math.max(80, maxLabelLength * 7);

  if (userInfoError)
    return <div className='error'>Failed to determine user permissions</div>;
  if (loading || userInfoLoading)
    return <div className='loading'>Loading dashboard data...</div>;
  if (error) return <div className='error'>{error}</div>;

  return (
    <div className='dashboard-page'>
      <div className='dashboard-header-container'>
        <div className='dashboard-header-row'>
          <h1>
            {isMultiOrg ? 'Dashboard - All Locations' : 'Dashboard'}
          </h1>
        </div>
        <div className='date-range-display'>
          <div className='filter-container'>
            <button
              className='filter-button'
              ref={buttonRef}
              onClick={handleFilterClick}
            >
              Filter
            </button>
            {filterOpen &&
              createPortal(
                <div
                  className='filter-popup'
                  style={{
                    position: 'absolute',
                    top: popupPos.top,
                    left: popupPos.left,
                  }}
                >
                  <h3>
                    {isMultiOrg
                      ? 'Filter by Date Range & Location'
                      : 'Filter by Date Range'}
                  </h3>
                  {dateError && <div className='date-error'>{dateError}</div>}

                  <div className='filter-inputs'>
                    <div className='filter-input-group'>
                      <label htmlFor='start'>Start Date</label>
                      <input
                        type='date'
                        id='start'
                        name='start'
                        value={tempDateRange.start}
                        onChange={handleDateChange}
                      />
                    </div>
                    <div className='filter-input-group'>
                      <label htmlFor='end'>End Date</label>
                      <input
                        type='date'
                        id='end'
                        name='end'
                        value={tempDateRange.end}
                        onChange={handleDateChange}
                      />
                    </div>
                  </div>

                  {isMultiOrg && (
                    <div className='suborg-filter-section'>
                      <label>Locations/Sites:</label>
                      <div className='suborg-select-all'>
                        <label>
                          <input
                            type='checkbox'
                            checked={
                              tempSelectedSuborgs.length ===
                              availableSuborgs.length
                            }
                            onChange={handleSelectAllSuborgs}
                          />
                          Select All Locations
                        </label>
                      </div>
                      <div className='suborg-checkboxes'>
                        {availableSuborgs.map((suborg) => (
                          <label key={suborg} className='suborg-checkbox'>
                            <input
                              type='checkbox'
                              checked={tempSelectedSuborgs.includes(suborg)}
                              onChange={() => handleSuborgChange(suborg)}
                            />
                            {suborg}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className='filter-actions'>
                    <button
                      className='filter-apply-button'
                      onClick={handleFilterApply}
                    >
                      Apply Filter
                    </button>
                    <button
                      className='filter-reset-button'
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                    <button
                      className='filter-cancel-button'
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>,
                document.body,
              )}
          </div>
          <span className='date-range-text'>{getDateRangeText()}</span>
        </div>
      </div>

      <div className='dashboard-metrics-row'>
        <div className='stat-card total-impaired'>
          <div className='stat-number'>{metrics.totalFlags}</div>
          <div className='stat-label'>Times Impairment Identified</div>
        </div>
        <div className='stat-card avg-impaired'>
          <div className='stat-number'>{metrics.flagsPerWeek}</div>
          <div className='stat-label'>
            AVG Times Impairment Identified Per Week
          </div>
        </div>
      </div>

      <div className='dashboard-charts-grid'>
        <div className='chart-container'>
          <h3>NUMBER OF CHECK INS</h3>
          <div className='chart'>
            <ChartOrMessage data={complianceChartData}>
              <ResponsiveContainer width='100%' height={200}>
                <LineChart data={complianceChartData}>
                  <CartesianGrid vertical={false} strokeDasharray={`3 3`} />
                  <XAxis
                    dataKey='monthLabel'
                    tick={{fontSize: 12}}
                    interval={0}
                    tickLine={false}
                  />
                  <YAxis allowDecimals={false} domain={[0, 'dataMax']} />
                  <Tooltip
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        return payload[0].payload.fullDate;
                      }
                      return label;
                    }}
                  />
                  <Line
                    type='monotone'
                    dataKey='value'
                    stroke='#000000'
                    activeDot={{r: 8}}
                    dot={false}
                    name='Check-ins'
                    connectNulls={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartOrMessage>
          </div>
        </div>

        <div className='chart-container'>
          <h3>IMPAIRED EMPLOYEES</h3>
          <div className='chart'>
            <ChartOrMessage
              message='No employees have been found impaired yet'
              data={flaggedChartData}
            >
              <ResponsiveContainer width='100%' height={200}>
                <LineChart data={flaggedChartData}>
                  <CartesianGrid vertical={false} strokeDasharray='3 3' />
                  <XAxis
                    dataKey='monthLabel'
                    tick={{fontSize: 12}}
                    interval={0}
                    tickLine={false}
                  />
                  <YAxis allowDecimals={false} domain={[0, 'dataMax']} />
                  <Tooltip
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        return payload[0].payload.fullDate;
                      }
                      return label;
                    }}
                  />
                  <Line
                    type='monotone'
                    dataKey='value'
                    stroke='#ff0000'
                    activeDot={{r: 8}}
                    dot={false}
                    name='Impaired Employees'
                    connectNulls={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartOrMessage>
          </div>
        </div>

        <div className='chart-container'>
          <h3>
            {isMultiOrg
              ? 'FREQUENTLY IMPAIRED (BY LOCATION)'
              : 'FREQUENTLY IMPAIRED'}
          </h3>
          <div
            className='chart'
            style={isMultiOrg ? {height: 'auto'} : undefined}
          >
            <ChartOrMessage
              message='No employees have been found impaired yet'
              data={filteredData.frequentlyFlaggedData}
            >
              <ResponsiveContainer
                width='100%'
                height={isMultiOrg ? xAxisHeight + 200 : 200}
              >
                <BarChart data={filteredData.frequentlyFlaggedData}>
                  <CartesianGrid vertical={false} strokeDasharray='3 3' />
                  <XAxis
                    dataKey='name'
                    {...(isMultiOrg
                      ? {
                          interval: 0,
                          height: xAxisHeight,
                          tick: ({x, y, payload}) => (
                            <g transform={`translate(${x},${y})`}>
                              <text
                                x={0}
                                y={0}
                                dy={4}
                                textAnchor='end'
                                fill='#666'
                                fontSize={12}
                                transform='rotate(-90)'
                              >
                                {payload.value}
                              </text>
                            </g>
                          ),
                        }
                      : {})}
                  />
                  <YAxis allowDecimals={false} domain={[0, 'dataMax']} />
                  <Tooltip />
                  <Bar
                    dataKey='value'
                    fill='#8884d8'
                    name='Times Impaired'
                    minPointSize={2}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartOrMessage>
          </div>
        </div>

        <div className='chart-container'>
          <h3>IMPAIRMENT BY DAY OF WEEK</h3>
          <div className='chart'>
            <ChartOrMessage
              message='No employees have been found impaired yet'
              data={filteredData.flagByDayData}
            >
              <ResponsiveContainer width='100%' height={200}>
                <BarChart data={filteredData.flagByDayData}>
                  <CartesianGrid vertical={false} strokeDasharray='3 3' />
                  <XAxis dataKey='day' />
                  <YAxis allowDecimals={false} domain={[0, 'dataMax']} />
                  <Tooltip
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(label, payload) => {
                      if (payload && payload.length > 0) {
                        return payload[0].payload.fullDay;
                      }
                      return label;
                    }}
                  />
                  <Bar
                    dataKey='value'
                    fill='#8884d8'
                    name='Flags'
                    minPointSize={2}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartOrMessage>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
